/* Iter 321 — API hors ligne : GET mis en cache (IndexedDB), mutations mises en file d'attente puis rejouées. */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { kvGet, kvSet, kvPatch, queueAdd, queueAll, queueDelete, queueUpdate, queueCount, metaGet, metaSet } from "./db";

export const cacheKey = (url, params) => (params && Object.keys(params).length ? `${url}?${new URLSearchParams(params).toString()}` : url);

const isNetworkError = (e) => !e?.response;
const listeners = new Set();
const state = { online: typeof navigator === "undefined" ? true : navigator.onLine, pending: 0, syncing: false, lastSync: null, lastError: null, rejected: [] };
const emit = () => listeners.forEach((l) => l({ ...state }));

async function refreshPending() {
  try { state.pending = await queueCount(); } catch { state.pending = 0; }
  emit();
}
if (typeof window !== "undefined") {
  window.addEventListener("online", () => { state.online = true; emit(); syncQueue(); });
  window.addEventListener("offline", () => { state.online = false; emit(); });
  document.addEventListener("visibilitychange", () => { if (document.visibilityState === "visible" && state.online) syncQueue(); });
  // Background Sync : le service worker réveille l'app pour rejouer la file d'attente
  navigator.serviceWorker?.addEventListener?.("message", (ev) => { if (ev.data?.type === "SYNC_QUEUE") syncQueue(); });
  metaGet("lastSync").then((v) => { state.lastSync = v; emit(); }).catch(() => {});
  metaGet("rejected").then((v) => { state.rejected = Array.isArray(v) ? v : []; emit(); }).catch(() => {});
  refreshPending();
}

const OP_LABELS = [
  [/\/tracking\/punch\/offline$/, "Pointage hors ligne"],
  [/^\/messages\//, "Message"],
  [/^\/personal\/tasks/, "Tâche"],
  [/^\/notifications/, "Notification"],
];
export const describeOp = (op) => (OP_LABELS.find(([re]) => re.test(op.url || ""))?.[1] || "Action") + (op.body?.mode ? ` (${op.body.mode === "start" ? "entrée" : "sortie"})` : "");

async function recordRejected(op, detail) {
  const entry = { id: op.id, url: op.url, method: op.method, label: describeOp(op), detail: String(detail || "Refusé par le serveur"), at: Date.now(), seen: false };
  state.rejected = [entry, ...(state.rejected || [])].slice(0, 20);
  await metaSet("rejected", state.rejected).catch(() => {});
  emit();
}

export async function markRejectedSeen() {
  state.rejected = (state.rejected || []).map((r) => ({ ...r, seen: true }));
  await metaSet("rejected", state.rejected).catch(() => {});
  emit();
}

export async function clearRejected() {
  state.rejected = [];
  await metaSet("rejected", []).catch(() => {});
  emit();
}

function requestBackgroundSync() {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
  navigator.serviceWorker.ready.then((reg) => reg.sync?.register("blitzz-sync")).catch(() => {});
}

/** Pré-charge une liste d'URL GET dans le cache hors ligne (ignore les échecs). */
export async function prefetchUrls(api, urls, onProgress = () => {}) {
  let done = 0;
  for (const u of urls) {
    try { await api.get(u); } catch { /* ignoré */ }
    done += 1;
    onProgress({ done, total: urls.length, url: u });
  }
  return done;
}

let _rawApi = null;
export const bindRawApi = (api) => { _rawApi = api; };

/** Rejoue la file d'attente dans l'ordre. S'arrête au premier échec réseau. */
export async function syncQueue() {
  if (state.syncing || !_rawApi || !state.online) return { done: 0 };
  state.syncing = true; state.lastError = null; emit();
  let done = 0;
  try {
    const ops = await queueAll();
    for (const op of ops) {
      try {
        await _rawApi.request({ method: op.method, url: op.url, data: op.body, headers: op.headers || {} });
        await queueDelete(op.id);
        done += 1;
      } catch (e) {
        if (isNetworkError(e)) { state.lastError = "Réseau indisponible"; break; }
        const status = e.response?.status;
        if (status === 401) { state.lastError = "Session expirée — reconnectez-vous"; break; }
        // 4xx/5xx métier : on abandonne l'opération (déjà appliquée, invalide…) pour ne pas bloquer la file
        op.attempts = (op.attempts || 0) + 1;
        op.last_error = e.response?.data?.detail || e.message;
        if (status >= 500 && op.attempts < 3) { await queueUpdate(op); break; }
        await queueDelete(op.id);
        await recordRejected(op, op.last_error);
      }
    }
    if (done) { state.lastSync = Date.now(); await metaSet("lastSync", state.lastSync); }
  } finally {
    state.syncing = false;
    await refreshPending();
  }
  return { done };
}

/** Enveloppe axios : GET réseau → cache ; hors ligne → cache ; POST/PUT/DELETE hors ligne → file d'attente. */
export function makeOfflineApi(raw, { scope = "", onQueued = null, noQueue = [] } = {}) {
  const get = async (url, config = {}) => {
    const key = cacheKey(url, config.params);
    if (config.responseType === "blob" || config.responseType === "arraybuffer") return raw.get(url, config);
    try {
      const r = await raw.get(url, config);
      kvSet(key, r.data, { scope }).catch(() => {});
      return r;
    } catch (e) {
      if (!isNetworkError(e) && e.response?.status !== 401) throw e;
      const cached = await kvGet(key);
      if (cached) return { data: cached.data, status: 200, fromCache: true, cachedAt: cached.ts };
      const err = new Error("Hors ligne — donnée non disponible localement");
      err.offline = true;
      throw err;
    }
  };
  const mutate = (method) => async (url, body, config = {}) => {
    try {
      return await raw.request({ method, url, data: body, ...config });
    } catch (e) {
      if (!isNetworkError(e)) throw e;
      if (body instanceof FormData || noQueue.some((re) => re.test(url))) {
        const err = new Error("Hors ligne — cette action nécessite une connexion");
        err.offline = true;
        err.response = { status: 0, data: { detail: body instanceof FormData ? "Envoi de fichier impossible hors ligne" : "Hors ligne — cette action nécessite une connexion" } };
        throw err;
      }
      const client_id = `off_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
      const payload = body && typeof body === "object" ? { ...body, client_id } : body;
      if (config.dedupe) {
        const existing = await queueAll();
        await Promise.all(existing.filter((o) => o.method === method && o.url === url).map((o) => queueDelete(o.id)));
      }
      await queueAdd({ method, url, body: payload, scope, client_id });
      await refreshPending();
      try { await onQueued?.({ method, url, body: payload, client_id }); } catch { /* cache optimiste indisponible */ }
      requestBackgroundSync();
      return { data: { ...(payload || {}), queued: true, client_id, created_at: new Date().toISOString() }, status: 202, queued: true };
    }
  };
  return {
    get,
    post: mutate("post"),
    put: mutate("put"),
    patch: mutate("patch"),
    delete: (url, config = {}) => mutate("delete")(url, undefined, config),
    request: raw.request.bind(raw),
    raw,
    patchCache: kvPatch,
    cacheKey,
  };
}

/** Hook : instance API hors ligne (stable) + état réseau/synchro. */
export function useOfflineApi(scope = "") {
  const { axiosAuth } = useAuth();
  const raw = axiosAuth();
  useEffect(() => { bindRawApi(raw); }, [raw]);
  return useMemo(() => makeOfflineApi(raw, { scope }), [raw, scope]);
}

export function useOfflineStatus() {
  const [s, setS] = useState({ ...state });
  useEffect(() => {
    listeners.add(setS);
    return () => listeners.delete(setS);
  }, []);
  useEffect(() => {
    const id = setInterval(() => { if (state.online && state.pending > 0) syncQueue(); }, 30000);
    return () => clearInterval(id);
  }, []);
  const sync = useCallback(() => syncQueue(), []);
  return { ...s, sync };
}

/* ── Préparation hors ligne d'une session ──────────────────────── */
export async function precacheScorm(api, contentId) {
  if (!("serviceWorker" in navigator) || !navigator.serviceWorker.controller) return 0;
  const r = await api.get(`/lms/scorm/${contentId}/files`);
  const base = `${process.env.REACT_APP_BACKEND_URL}/api/lms/scorm/${contentId}/file/`;
  const urls = (r.data?.files || []).map((f) => base + f);
  await new Promise((resolve) => {
    const onMsg = (ev) => { if (ev.data?.type === "PRECACHE_DONE" && ev.data.key === contentId) { navigator.serviceWorker.removeEventListener("message", onMsg); resolve(); } };
    navigator.serviceWorker.addEventListener("message", onMsg);
    navigator.serviceWorker.controller.postMessage({ type: "PRECACHE_SCORM", key: contentId, urls });
    setTimeout(() => { navigator.serviceWorker.removeEventListener("message", onMsg); resolve(); }, 120000);
  });
  return urls.length;
}

/** Télécharge tout ce qu'il faut pour travailler hors ligne sur une session (formateur ou stagiaire). */
export async function prepareSessionOffline(api, sessionId, role, onProgress = () => {}) {
  const steps = [];
  const isTrainer = role === "trainer";
  steps.push(["Session", () => api.get(isTrainer ? `/lms/sessions/${sessionId}` : `/student/sessions/${sessionId}`)]);
  if (isTrainer) {
    steps.push(["Stagiaires", () => api.get(`/lms/sessions/${sessionId}/enrollments`)]);
    steps.push(["Progression", () => api.get(`/lms/sessions/${sessionId}/trainer-progress`)]);
    steps.push(["Évaluations", () => api.get(`/formateur/sessions/${sessionId}/evaluations`).catch(() => null)]);
  }
  steps.push(["Messagerie", () => api.get(`/lms/sessions/${sessionId}/chat`)]);
  steps.push(["Émargement", () => api.get(`/lms/sessions/${sessionId}/attendance/signatures`)]);
  steps.push(["Identité visuelle", () => api.get("/me/app-config", { params: { session_id: sessionId } })]);
  let sessionData = null;
  let i = 0;
  for (const [label, fn] of steps) {
    onProgress({ label, done: i, total: steps.length });
    const r = await fn();
    if (label === "Session") sessionData = r?.data;
    i += 1;
  }
  const sequences = (isTrainer ? sessionData?.sequences : sessionData?.session?.sequences) || [];
  let scormFiles = 0;
  for (let k = 0; k < sequences.length; k += 1) {
    const seq = sequences[k];
    onProgress({ label: `Module ${k + 1}/${sequences.length} — ${seq.title || ""}`, done: steps.length + k, total: steps.length + sequences.length });
    await api.get(`/student/sessions/${sessionId}/sequences/${seq.sequence_id}/content`);
    if (seq.kind === "scorm") {
      try {
        await api.get(`/lms/student/enrollments/${sessionData?.enrollment?.enrollment_id || "_"}/scorm/${seq.sequence_id}/tracking`).catch(() => {});
        const n = await precacheScorm(api, seq.sequence_id);
        scormFiles += n;
        await markScormOffline(seq.sequence_id, n);
      } catch { /* module SCORM indisponible */ }
    }
  }
  const prepared = (await metaGet("preparedSessions")) || {};
  prepared[sessionId] = { at: Date.now(), role, modules: sequences.length, scormFiles };
  await metaSet("preparedSessions", prepared);
  onProgress({ label: "Terminé", done: 1, total: 1, finished: true });
  return prepared[sessionId];
}

export async function markScormOffline(contentId, files) {
  const m = (await metaGet("scormOffline")) || {};
  m[contentId] = { at: Date.now(), files };
  await metaSet("scormOffline", m);
}
export async function getScormOffline(contentId) {
  const m = (await metaGet("scormOffline")) || {};
  return m[contentId] || null;
}

export async function getPreparedInfo(sessionId) {
  const prepared = (await metaGet("preparedSessions")) || {};
  return prepared[sessionId] || null;
}
